import { envConfigs } from '@/config';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';

export const SITE_ORIGIN = 'https://controlresonanttools.online';
export interface SeoPage {
  title: string;
  description: string;
  url: string;
  ogImage?: string;
  indexable?: boolean;
  schema?: string;
  updatedAt?: string;
  locale?: string;
  h1?: string;
  breadcrumbLabel?: string;
}
export function localePath(path: string, locale: string) {
  return locale === 'zh' ? '/zh' + (path === '/' ? '' : path) : path;
}
export function notFoundHead() {
  return {
    meta: [
      { title: `${m['control.not_found.title']()} | ${envConfigs.app_name}` },
      { name: 'description', content: m['control.not_found.body']() },
      { name: 'robots', content: 'noindex,follow' },
    ],
  };
}
export function canonicalUrl(path: string) {
  return new URL(path.split(/[?#]/)[0], SITE_ORIGIN).href;
}
export function seoHead(page: SeoPage) {
  const locale = page.locale || getLocale();
  const canonical = canonicalUrl(localePath(page.url, locale));
  const image = canonicalUrl(
    page.ogImage ||
      (locale === 'zh' ? '/og/zh/og-default.png' : '/og/og-default.png')
  );
  return {
    meta: [
      { title: page.title },
      { name: 'description', content: page.description },
      {
        name: 'robots',
        content:
          page.indexable === false
            ? 'noindex,follow'
            : 'index,follow,max-image-preview:large',
      },
      { property: 'og:title', content: page.title },
      { property: 'og:description', content: page.description },
      { property: 'og:url', content: canonical },
      {
        property: 'og:type',
        content: page.schema === 'Article' ? 'article' : 'website',
      },
      { property: 'og:site_name', content: envConfigs.app_name },
      { property: 'og:locale', content: locale === 'zh' ? 'zh_CN' : 'en_US' },
      {
        property: 'og:locale:alternate',
        content: locale === 'zh' ? 'en_US' : 'zh_CN',
      },
      { property: 'og:image', content: image },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: page.title },
      { name: 'twitter:description', content: page.description },
      { name: 'twitter:image', content: image },
    ],
    links: [
      { rel: 'canonical', href: canonical },
      ...(['en', 'zh', 'x-default'] as const).map((lang) => ({
        rel: 'alternate',
        hrefLang: lang === 'zh' ? 'zh-CN' : lang,
        href: canonicalUrl(
          localePath(page.url, lang === 'x-default' ? 'en' : lang)
        ),
      })),
    ],
  };
}
export interface Crumb {
  label: string;
  href?: string;
}
export function pageCrumbs(page: SeoPage): Crumb[] {
  const zh = (page.locale || getLocale()) === 'zh';
  const crumbs: Crumb[] = [{ label: zh ? '首页' : 'Home', href: '/' }];
  const parent = '/' + page.url.split('/')[1];
  if (parent === '/guides') crumbs.push({ label: zh ? '攻略' : 'Guides' });
  if (page.url.split('/').filter(Boolean).length > 1 && parent !== '/guides')
    crumbs.push({
      label: zh
        ? {
            puzzles: '谜题',
            abilities: '能力选择',
            quests: '任务攻略',
            trophies: '奖杯',
          }[parent.slice(1)] || parent.slice(1)
        : parent.slice(1).replace(/^./, (letter) => letter.toUpperCase()),
      href: parent,
    });
  if (page.url !== '/')
    crumbs.push({
      label: page.breadcrumbLabel || page.h1 || page.title.split(' – ')[0],
      href: page.url,
    });
  return crumbs;
}
export function pageSchema(page: SeoPage, crumbs = pageCrumbs(page)) {
  const locale = page.locale || getLocale();
  const url = (path: string) => canonicalUrl(localePath(path, locale));
  const base = {
    '@context': 'https://schema.org',
    '@type': page.schema || 'WebPage',
    name: page.title,
    description: page.description,
    url: url(page.url),
    inLanguage: locale === 'zh' ? 'zh-CN' : 'en',
  };
  const entity =
    page.schema === 'WebSite'
      ? {
          ...base,
          name: envConfigs.app_name,
          ...(locale === 'zh' ? { alternateName: '《控制: 共振》工具站' } : {}),
        }
      : page.schema === 'Article'
        ? {
            ...base,
            headline: page.title,
            image: canonicalUrl(
              page.ogImage ||
                (locale === 'zh'
                  ? '/og/zh/og-default.png'
                  : '/og/og-default.png')
            ),
            dateModified: page.updatedAt,
            author: { '@type': 'Organization', name: envConfigs.app_name },
            mainEntityOfPage: url(page.url),
          }
        : page.schema === 'WebApplication'
          ? {
              ...base,
              applicationCategory: 'GameApplication',
              operatingSystem: 'Web browser',
              isAccessibleForFree: true,
            }
          : base;
  return page.url === '/'
    ? [entity]
    : [
        entity,
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: crumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.label,
            ...(crumb.href ? { item: url(crumb.href) } : {}),
          })),
        },
      ];
}
export function safeJsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}
